
import { supabase } from "@/integrations/supabase/client";

// Admin grants premium status to a listing
export const adminGrantPremium = async (
  listingId: number,
  adminId: string,
  adminNotes?: string
): Promise<boolean> => {
  try {
    // Calculate expiration date - 28 days from now
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 28);

    const { error } = await supabase.from("premium_payments").insert({
      listing_id: listingId,
      user_id: adminId,
      admin_granted: true,
      granted_by: adminId,
      admin_notes: adminNotes || '',
      expires_at: expiresAt.toISOString(),
      amount: 0.00, // No cost for admin-granted premium
      from_voucher: false
    });

    if (error) throw error;

    // Update the listing to premium status
    const { error: updateError } = await supabase
      .from("listings")
      .update({ is_premium: true })
      .eq("id", listingId);

    if (updateError) throw updateError;

    return true;
  } catch (error) {
    console.error("Error granting admin premium:", error);
    return false;
  }
};

// Admin removes premium status from a listing
export const adminRemovePremium = async (
  listingId: number,
  adminId: string
): Promise<boolean> => {
  try {
    // Update the listing to remove premium status
    const { error: updateError } = await supabase
      .from("listings")
      .update({ is_premium: false })
      .eq("id", listingId);

    if (updateError) throw updateError;

    return true;
  } catch (error) {
    console.error("Error removing admin premium:", error);
    return false;
  }
};

// Get premium payment details for a listing (admin view)
export const getListingPremiumDetails = async (
  listingId: number
): Promise<{
  isPremium: boolean;
  isAdminGranted: boolean;
  grantedBy?: string;
  adminNotes?: string;
  expiresAt?: Date;
} | null> => {
  try {
    const { data, error } = await supabase
      .from("premium_payments")
      .select("*")
      .eq("listing_id", listingId)
      .order("created_at", { ascending: false })
      .limit(1);

    if (error) throw error;

    if (!data || data.length === 0) {
      return {
        isPremium: false,
        isAdminGranted: false
      };
    }

    const payment = data[0];

    // If admin granted, fetch the admin's profile separately
    let grantedByUsername = undefined;
    if (payment.admin_granted && payment.granted_by) {
      const { data: profileData } = await supabase
        .from("profiles")
        .select("username, email")
        .eq("id", payment.granted_by)
        .single();

      grantedByUsername = profileData?.username || profileData?.email;
    }

    return {
      isPremium: true,
      isAdminGranted: payment.admin_granted || false,
      grantedBy: grantedByUsername,
      adminNotes: payment.admin_notes,
      expiresAt: payment.expires_at ? new Date(payment.expires_at) : undefined
    };
  } catch (error) {
    console.error("Error getting premium details:", error);
    return null;
  }
};
